import { type MouseEvent, memo, useCallback } from 'react';

import { PinIcon } from '@air/design-icons';
import { Tooltip } from '@air/primitive-tooltip';
import { cn } from '@air/utils-tailwind';

import { useMainSideNavStore } from '~/components/v2/store/features/workspace/useMainSideNavStore';

export const MainSideNavPinButton = memo(() => {
  const isPinned = useMainSideNavStore((state) => state.pinned);
  const togglePinned = useMainSideNavStore((state) => state.togglePinned);

  const handleTogglePinned = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      togglePinned();

      // Unpin leaves focus on the control; blur so the overlay can collapse
      // on mouse leave instead of waiting for an outside click. Only for
      // pointer activation — blurring on keyboard Enter/Space would drop
      // focus and the user couldn't press it again to re-pin.
      if (isPinned && event.detail !== 0) {
        const button = event.currentTarget;
        queueMicrotask(() => {
          button.blur();
        });
      }
    },
    [isPinned, togglePinned],
  );

  return (
    <Tooltip label="Pin Side Nav" side="right">
      <button
        aria-label="Pin Side Nav"
        aria-pressed={isPinned}
        className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-8 border-none bg-transparent p-0 ads:text-icon-default text-gray-10 outline-none ads:hover:bg-hover-subtle hover:bg-gray-2 ads:hover:text-icon-emphasis hover:text-gray-12 ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9 active:bg-gray-2 ads:active:bg-hover-subtle"
        data-testid="MAIN_SIDE_NAV_PIN"
        onClick={handleTogglePinned}
        type="button"
      >
        <PinIcon className={cn(isPinned && 'fill-current')} size="sm" />
      </button>
    </Tooltip>
  );
});

MainSideNavPinButton.displayName = 'MainSideNavPinButton';
